import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RecycleBinModal = ({
  isOpen,
  onClose,
  activeTab,
  onRestore,
  onPermanentDelete,
  deletedItems,
  loading,
}) => {
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    if (!isOpen) setConfirmDeleteId(null);
  }, [isOpen]);

  if (!isOpen) return null;

  const getItemLabel = (item) => {
    return (
      item?.name ||
      item?.itemName ||
      item?.softwareName ||
      item?.vendorName ||
      item?.resourceName ||
      'Unnamed Record'
    );
  };

  const getItemDetails = (item) => {
    switch (activeTab) {
      case 'fixed-expenses': case'variable-expenses':
        return `${item?.category || ''} · OMR ${parseFloat(item?.monthlyCost || 0)?.toFixed(3)}/mo`;
      case 'materials':
        return `${item?.category || ''} · ${item?.uom || ''} · OMR ${parseFloat(item?.unitCost || 0)?.toFixed(3)}`;
      case 'engineers': case'outsource':
        return `${item?.role || ''} · ${item?.specialization || ''} · OMR ${parseFloat(item?.monthlySalary || 0)?.toFixed(3)}/mo`;
      case 'software':
        return `${item?.vendor || ''} · ${item?.licenseType || ''} · OMR ${parseFloat(item?.cost || 0)?.toFixed(3)}`;
      case 'vendors':
        return `${item?.category || ''} · ${item?.email || ''}`;
      default:
        return '';
    }
  };

  const formatDeletedAt = (ts) => {
    if (!ts) return '—';
    try {
      return new Date(ts)?.toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return ts;
    }
  };

  const TAB_LABELS = {
    'fixed-expenses': 'Fixed Expenses',
    'variable-expenses': 'Variable Expenses',
    'materials': 'Materials',
    'engineers': 'Engineers',
    'software': 'Software Licenses',
    'vendors': 'Vendors',
    'outsource': 'Outsource Resources',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-surface border border-border rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-warning/10 flex items-center justify-center">
              <Icon name="Trash2" size={18} className="text-warning" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Recycle Bin</h2>
              <p className="text-xs text-muted-foreground">{TAB_LABELS?.[activeTab]} — deleted records</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : deletedItems?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Icon name="CheckCircle" size={48} className="text-success mb-3" />
              <p className="text-sm font-medium text-foreground">Recycle bin is empty</p>
              <p className="text-xs text-muted-foreground mt-1">No deleted records found for this module.</p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground mb-3">
                {deletedItems?.length} deleted record{deletedItems?.length !== 1 ? 's' : ''} found. Restore to recover or permanently delete to remove forever.
              </p>
              {deletedItems?.map((item) => (
                <div
                  key={item?.id}
                  className="flex items-start justify-between gap-4 p-4 rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-foreground truncate">{getItemLabel(item)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{getItemDetails(item)}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Icon name="Clock" size={11} />
                        Deleted: {formatDeletedAt(item?.deletedAt)}
                      </span>
                      {item?.deletedByEmail && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Icon name="User" size={11} />
                          {item?.deletedByEmail}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="xs"
                      iconName="RotateCcw"
                      onClick={() => onRestore(item?.id)}
                      title="Restore this record"
                    >
                      Restore
                    </Button>
                    {confirmDeleteId === item?.id ? (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="danger"
                          size="xs"
                          iconName="Trash2"
                          onClick={() => { onPermanentDelete(item?.id); setConfirmDeleteId(null); }}
                        >
                          Confirm
                        </Button>
                        <Button
                          variant="ghost"
                          size="xs"
                          iconName="X"
                          onClick={() => setConfirmDeleteId(null)}
                        />
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        size="xs"
                        iconName="Trash2"
                        onClick={() => setConfirmDeleteId(item?.id)}
                        title="Permanently delete"
                        className="text-destructive hover:text-destructive"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            <Icon name="Info" size={12} className="inline mr-1" />
            Restored records will reappear in the active list immediately.
          </p>
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

export default RecycleBinModal;
